/**
 * HintIcon — the app's one way to attach an explanation to a label.
 *
 * A small info glyph that reveals its text on hover or focus. The look
 * and behaviour are lifted verbatim from the permission rows in the
 * user-access form, which is where this pattern was established: 16px
 * glyph in `--qhs-text-3`, brand colour on hover and focus, `cursor:
 * help`, and the shared Tooltip (never the native `title` attribute —
 * its ~1s browser delay reads as no tooltip at all).
 *
 * It exists so that adding a hint somewhere new does not mean copying
 * that styled block again and quietly inventing a third look.
 *
 * On touch the shared Tooltip opens on long-press. That is a weak
 * affordance, so a hint should stay an aid to understanding, never the
 * only place a rule is stated.
 */
// Imported from their own modules rather than the `@shared/ui` barrel:
// the barrel exports this component, so going through it would close a
// cycle and leave `styled` undefined at module-eval time.
import { styled } from '../styled';
import Tooltip from '../Tooltip/Tooltip';
import { InfoIcon } from '../icons';

const HintButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: 6,
  padding: 0,
  width: 16,
  height: 16,
  border: 'none',
  background: 'transparent',
  color: 'var(--qhs-text-3)',
  cursor: 'help',
  transition: 'color 120ms ease',
  '&:hover, &:focus-visible': { color: 'var(--qhs-brand, #00A8FF)' },
  '&:focus-visible': { outline: 'none' },
  '& svg': { width: 16, height: 16 },
});

const HintIcon = ({ hint, placement = 'top', className }) => {
  if (!hint) return null;

  return (
    <Tooltip title={hint} placement={placement}>
      {/*
        A real button, so the hint is reachable by keyboard. The click
        is swallowed: the glyph explains, it never acts — and inside a
        label or a header row a stray click would toggle whatever it
        sits next to.
      */}
      <HintButton
        type="button"
        aria-label={hint}
        className={className}
        onClick={e => e.preventDefault()}
      >
        <InfoIcon />
      </HintButton>
    </Tooltip>
  );
};

export default HintIcon;
